import { useCallback } from 'react';

import { useMutation } from '@apollo/client/react';
import type { DeepPartial } from '@apollo/client/utilities';
import type { DocumentNode, FieldNode, FragmentDefinitionNode } from 'graphql';

import type {
  Cma_Reports_Set_Input,
  Lots_Set_Input,
} from '@graphql/__generated__/graphql';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import {
  UPDATE_BROCHURE_COVER,
  UPDATE_BROCHURE_DESCRIPTION,
  UPDATE_PROPERTY_DETAILS_BROCHURE,
  UPDATE_STEP_PHOTOS_BROCHURE,
  UPDATE_STEP_POI_BROCHURE,
} from '@modules/reports/brochure-report/brochureReportsQueries';
import {
  UPDATE_CAP_RATE_VALUATION,
  UPDATE_CMA_COVER,
  UPDATE_CMA_INTRODUCTION,
  UPDATE_CMA_MARKET_DATA,
  UPDATE_COMPARABLES_LIST,
  UPDATE_DESCRIPTION,
  UPDATE_FINAL,
  UPDATE_HEDONISTIC_VALUATION,
  UPDATE_INTRINSIC_VALUATION,
  UPDATE_POTENTIAL_BUYERS,
  UPDATE_PROPERTY_DETAILS,
  UPDATE_STEP_PHOTOS,
  UPDATE_STEP_POINTS_OF_INTEREST,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import type { StepCoverPropertyImageType } from '@modules/reports/shared/CoverPhotoSelector/CoverPhotoSelector';

export type CMAPageId =
  | 'page-cover'
  | 'page-introduction'
  | 'page-property'
  | 'page-description'
  | 'page-photos'
  | 'page-point-of-interest'
  | 'page-market-data'
  | 'page-hedonistic-valuation'
  | 'page-comparables-list'
  | 'page-rental-comparables-list'
  | 'page-transactions'
  | 'page-cap-rate-valuation'
  | 'page-intrinsic-valuation'
  | 'page-final'
  | 'page-potential-buyers'
  | 'page-agent-profile'
  | 'page-agency-profile'
  | 'page-client-testimonials'
  | 'page-append-files'
  | 'page-share';

export type ReportType = 'cma_report' | 'lot_brochure';

export type UpdateReportReturn<T> = readonly [
  (input: T) => Promise<any>,
  boolean,
];

export const useUpdateReport = <
  T,
  PageIdType extends string,
  MutationTupleType extends readonly [
    (options: any) => Promise<any>,
    { loading: boolean },
  ],
>(
  reportId: string,
  page: PageIdType,
  mutationsMap: Record<PageIdType, MutationTupleType>,
): UpdateReportReturn<T> => {
  const [mutation, { loading: updating }] = mutationsMap[page];
  const { setError } = useReportContext();

  const updateReport = useCallback(
    async (input: T) =>
      await mutation({
        variables: {
          id: reportId,
          input,
        },
        onError: setError,
      }),
    [reportId, mutation, setError],
  );

  return [updateReport, updating] as const;
};

export const useUpdateCmaReport = (
  cmaReportId: string,
  page: Exclude<
    CMAPageId,
    | 'page-append-files'
    | 'page-share'
    | 'page-agent-profile'
    | 'page-agency-profile'
    | 'page-client-testimonials'
    | 'page-transactions'
  >,
): UpdateReportReturn<Cma_Reports_Set_Input> => {
  const cmaMutations = {
    'page-cover': useMutation(UPDATE_CMA_COVER),
    'page-introduction': useMutation(UPDATE_CMA_INTRODUCTION),
    'page-property': useMutation(UPDATE_PROPERTY_DETAILS),
    'page-description': useMutation(UPDATE_DESCRIPTION),
    'page-photos': useMutation(UPDATE_STEP_PHOTOS),
    'page-point-of-interest': useMutation(UPDATE_STEP_POINTS_OF_INTEREST),
    'page-market-data': useMutation(UPDATE_CMA_MARKET_DATA),
    'page-hedonistic-valuation': useMutation(UPDATE_HEDONISTIC_VALUATION),
    'page-cap-rate-valuation': useMutation(UPDATE_CAP_RATE_VALUATION),
    'page-intrinsic-valuation': useMutation(UPDATE_INTRINSIC_VALUATION),
    'page-potential-buyers': useMutation(UPDATE_POTENTIAL_BUYERS),
    'page-comparables-list': useMutation(UPDATE_COMPARABLES_LIST),
    'page-rental-comparables-list': useMutation(UPDATE_COMPARABLES_LIST),
    'page-final': useMutation(UPDATE_FINAL),
  } as const;

  return useUpdateReport<
    Cma_Reports_Set_Input,
    keyof typeof cmaMutations,
    (typeof cmaMutations)[keyof typeof cmaMutations]
  >(cmaReportId, page, cmaMutations);
};

export type ListingBrochurePageId =
  | 'page-cover'
  | 'page-description'
  | 'page-property'
  | 'page-point-of-interest'
  | 'page-photos'
  | 'page-append-files'
  | 'page-share';

export const useUpdateListingBrochure = (
  listingBrochureId: string,
  page: Exclude<ListingBrochurePageId, 'page-append-files' | 'page-share'>,
): UpdateReportReturn<Lots_Set_Input> => {
  const listingBrochureMutations = {
    'page-cover': useMutation(UPDATE_BROCHURE_COVER),
    'page-description': useMutation(UPDATE_BROCHURE_DESCRIPTION),
    'page-property': useMutation(UPDATE_PROPERTY_DETAILS_BROCHURE),
    'page-point-of-interest': useMutation(UPDATE_STEP_POI_BROCHURE),
    'page-photos': useMutation(UPDATE_STEP_PHOTOS_BROCHURE),
  } as const;

  return useUpdateReport<
    Lots_Set_Input,
    keyof typeof listingBrochureMutations,
    (typeof listingBrochureMutations)[keyof typeof listingBrochureMutations]
  >(listingBrochureId, page, listingBrochureMutations);
};

type propertyImages =
  | DeepPartial<{
      id: string;
      image: {
        id: string;
        url: string | null;
      };
    }>
  | undefined;

export const getFilteredPropertyImages = (
  propertyImages: propertyImages[] | undefined,
) => {
  const result: StepCoverPropertyImageType[] = [];

  if (propertyImages == null) {
    return result;
  }

  for (const propertyImage of propertyImages) {
    if (
      propertyImage &&
      typeof propertyImage.id === 'string' &&
      propertyImage.image &&
      typeof propertyImage.image.id === 'string' &&
      typeof propertyImage.image.url === 'string'
    ) {
      result.push({
        id: propertyImage.id,
        image: {
          id: propertyImage.image.id,
          url: propertyImage.image.url,
        },
      });
    }
  }

  return result;
};

export const extractFieldsFromFragment = (
  fragment: DocumentNode,
  excludeFields: string[] = ['id'],
): string[] => {
  try {
    const fragmentDef = fragment.definitions[0] as FragmentDefinitionNode;
    const selections = fragmentDef?.selectionSet?.selections || [];

    return selections
      .map(selection => {
        if (selection.kind === 'Field') {
          return (selection as FieldNode).name.value;
        }
        return null;
      })
      .filter(
        (name): name is string =>
          typeof name === 'string' && !excludeFields.includes(name),
      );
  } catch (e) {
    console.warn('Failed to extract fields from fragment', e);
    return [];
  }
};
